export const getSystemData = (): {
  device_os_name: string;
  device_os_version: string;
  device_browser_name: string;
  device_browser_version: string;
  device_type: string;
  device_marketing_name: string;
  device_model: string;
  device_vendor: string;
} => {
  let device_os_name = '';
  let device_os_version = '';
  let device_browser_name = '';
  let device_browser_version = '';
  let device_type = '';
  let device_marketing_name = '';
  let device_model = '';
  let device_vendor = '';

  detectDeviceTypeFromUA();

  // Try to use modern userAgentData API
  if (
    'userAgentData' in navigator &&
    navigator.userAgentData &&
    typeof navigator.userAgentData === 'object'
  ) {
    const uaData = navigator.userAgentData as any;

    // Get platform name
    if ('platform' in uaData) {
      device_os_name = uaData.platform;
    }

    // Get browser version
    if ('brands' in uaData && Array.isArray(uaData.brands)) {
      const browserInfo = uaData.brands.find(
        (brand: any) =>
          brand.brand?.includes('Chrome') ||
          brand.brand?.includes('Edge') ||
          brand.brand?.includes('Firefox')
      );
      if (browserInfo) {
        device_browser_name = browserInfo.brand || '';
        device_browser_version = browserInfo.version || '';
      }
    }

    parseUserAgentForOsVersion();
    parseUserAgentForDeviceInfo();

    // Try to get detailed OS info
    if (
      'getHighEntropyValues' in uaData &&
      typeof uaData.getHighEntropyValues === 'function'
    ) {
      uaData
        .getHighEntropyValues([
          'platformVersion',
          'model',
          'architecture',
          'bitness',
        ])
        .then((highEntropyData: any) => {
          if (highEntropyData?.platformVersion) {
            device_os_version = highEntropyData.platformVersion;
          }

          if (highEntropyData?.model) {
            device_model = highEntropyData.model;
            device_marketing_name = highEntropyData.model;

            // Try to extract vendor from model string
            extractVendorFromModel(device_model);
          }
        });
    } else {
      parseUserAgentForOsVersion();
      parseUserAgentForDeviceInfo();
    }
  }

  function extractVendorFromModel(modelString: string) {
    // Extract common vendors from model string
    const commonVendors = [
      'Samsung',
      'Apple',
      'Google',
      'Pixel',
      'Huawei',
      'Xiaomi',
      'Sony',
      'LG',
      'Motorola',
      'Nokia',
      'OnePlus',
      'ASUS',
      'Lenovo',
      'Dell',
      'HP',
      'Acer',
      'Microsoft',
      'Surface',
    ];

    const found = commonVendors.find((vendor) =>
      modelString.toLowerCase().includes(vendor.toLowerCase())
    );

    if (found) {
      device_vendor = found;
      // Special case for Google Pixel
      if (found === 'Pixel') {
        device_vendor = 'Google';
      }
    }
  }

  function detectDeviceTypeFromUA() {
    const ua = navigator.userAgent;

    // Check for tablets first (some tablets report as both mobile and tablet)
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      device_type = 'tablet';
    }
    // Check for mobile devices
    else if (
      /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|Opera Mini/i.test(
        ua
      )
    ) {
      device_type = 'mobile';
    } else {
      device_type = 'desktop';
    }
  }

  // UA string parsing function for OS info
  function parseUserAgentForOsVersion() {
    const ua = navigator.userAgent;

    // Extract OS version based on OS name
    if (device_os_name === 'Windows' || /Windows/.test(ua)) {
      const winMatch = ua.match(/Windows NT (\d+\.\d+)/);
      device_os_version = winMatch ? winMatch[1] : '';
    } else if (device_os_name === 'macOS' || /Macintosh|Mac OS X/.test(ua)) {
      const macMatch = ua.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
      device_os_version = macMatch ? macMatch[1].replace(/_/g, '.') : '';
    } else if (device_os_name === 'Android' || /Android/.test(ua)) {
      const androidMatch = ua.match(/Android (\d+\.\d+)/);
      device_os_version = androidMatch ? androidMatch[1] : '';
    } else if (device_os_name === 'iOS' || /iPhone|iPad|iPod/.test(ua)) {
      const iosMatch = ua.match(/OS (\d+[._]\d+[._]?\d*)/);
      device_os_version = iosMatch ? iosMatch[1].replace(/_/g, '.') : '';
    }
  }

  function parseUserAgentForDeviceInfo() {
    const ua = navigator.userAgent;

    // Extract device info based on device type
    if (device_type === 'mobile' || device_type === 'tablet') {
      // Android device detection
      if (/Android/.test(ua)) {
        const modelMatch = ua.match(/; ([^;)]+) Build\//);
        if (modelMatch) {
          device_model = modelMatch[1];
          device_marketing_name = device_model; // Usually the same for Android

          // Try to extract vendor from model string
          extractVendorFromModel(device_model);

          // Common Android vendors if not detected from model
          if (!device_vendor) {
            if (/Samsung|SM-|Galaxy/.test(ua)) device_vendor = 'Samsung';
            else if (/Pixel|Google/.test(ua)) device_vendor = 'Google';
            else if (/Huawei|HUAWEI/.test(ua)) device_vendor = 'Huawei';
            else if (/Xiaomi|MI /.test(ua)) device_vendor = 'Xiaomi';
            else if (/Sony|Xperia/.test(ua)) device_vendor = 'Sony';
            else if (/LG|LG-/.test(ua)) device_vendor = 'LG';
            else if (/Motorola|Moto /.test(ua)) device_vendor = 'Motorola';
            else if (/Nokia/.test(ua)) device_vendor = 'Nokia';
            else if (/OnePlus/.test(ua)) device_vendor = 'OnePlus';
            else device_vendor = 'Unknown Android';
          }
        }
      }
      // iOS device detection
      else if (/iPhone|iPad|iPod/.test(ua)) {
        if (/iPhone/.test(ua)) {
          device_marketing_name = 'iPhone';
          // Attempt to identify iPhone model (simplified)
          if (/iPhone1[0-4]/.test(ua)) {
            device_model = 'iPhone X or newer';
          } else if (/iPhone[7-9]/.test(ua)) {
            device_model = 'iPhone 6-8 series';
          } else {
            device_model = 'iPhone';
          }
        } else if (/iPad/.test(ua)) {
          device_marketing_name = 'iPad';
          device_model = 'iPad';
        } else if (/iPod/.test(ua)) {
          device_marketing_name = 'iPod';
          device_model = 'iPod';
        }
      }
    }
    // For desktop, identify basic platform
    else {
      if (/Windows/.test(ua)) {
        device_marketing_name = 'PC';
        device_model = 'Windows PC';
        device_vendor = 'PC';
      } else if (/Macintosh|Mac OS X/.test(ua)) {
        device_marketing_name = 'Mac';
        device_model = 'Apple Mac';
        device_vendor = 'Apple';
      } else if (/Linux/.test(ua)) {
        device_marketing_name = 'PC';
        device_model = 'Linux PC';
        device_vendor = 'PC';
      } else {
        device_marketing_name = 'PC';
        device_model = 'Unknown PC';
        device_vendor = 'Unknown';
      }
    }
  }

  return {
    device_os_name,
    device_os_version,
    device_browser_name,
    device_browser_version,
    device_type,
    device_marketing_name,
    device_model,
    device_vendor,
  };
};
